import {
  Align,
  Arrange,
  Container,
  Icon,
  IconButton,
  Spacer,
  SvgClose,
  SvgPlay,
  SvgSearch,
  Text,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import Downshift from 'downshift';
import moment from 'moment';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';

import {
  GLOBAL_SEARCH_CLICKTHROUGH,
  GLOBAL_SEARCH_QUERIED,
} from 'constants/events';
import * as analytics from 'utilities/analytics';
import { getGraphQLClient } from 'utilities/graphql';
import * as sentry from 'utilities/sentry';
import { secondsToVideoTS } from 'utilities/time';

import gqlSearchQuery from '../../creators/Search.graphql';
import { hideSearchModal } from '../../actions/searchModal';

import './styles.less';

function SearchModal() {
  const dispatch = useDispatch();
  const { show } = useSelector(state => state.searchModal);

  const [query, setQuery] = useState('');
  /**
   * Group multiples state variables in a single reducer to avoid useless re-renderings
   * and support partial update of the state
   */
  const [results, updateResults] = useReducer(
    (state, action) => ({
      ...state,
      ...action,
    }),
    {
      query: '',
      list: [],
      loading: false,
    }
  );
  const inputElement = useRef(null);

  const [queryDebounced] = useDebounce(query, 250); // only used for graphQL query

  // Only display a loading UI when we are loading and the debounced loading reached the true value
  const [loadingDebounced] = useDebounce(results.loading, 200);
  const uiLoading = results.loading && loadingDebounced.loading;

  const noResultsFound =
    !results.loading && results.list.length === 0 && results.query.length > 0;

  const gqlClient = useMemo(() => getGraphQLClient(), []);

  useEffect(() => {
    if (show) {
      focusInput();
    }
  }, [show]);

  useEffect(() => {
    updateResults({
      query: queryDebounced,
      loading: true,
    });

    gqlClient
      .query({
        query: gqlSearchQuery,
        variables: {
          searchQuery: queryDebounced,
        },
      })
      .then(res => {
        updateResults({
          query: queryDebounced,
          list: res.data.search,
          loading: false,
        });
        analytics.track(GLOBAL_SEARCH_QUERIED, {
          query: queryDebounced,
          totalResults: res.data.search.length,
        });
      })
      .catch(error => {
        sentry.logException(error, 'Error in search');

        updateResults({
          query: '',
          list: [],
          loading: false,
        });
      });
  }, [gqlClient, queryDebounced]);

  function focusInput() {
    inputElement.current.focus();
  }

  function resetInput() {
    setQuery('');
    focusInput();
  }

  function closeModal() {
    dispatch(hideSearchModal());
  }

  function handleClickThrough(event, id, position) {
    event.stopPropagation();

    analytics.track(GLOBAL_SEARCH_CLICKTHROUGH, {
      query: queryDebounced,
      resultId: id,
      position,
    });
  }

  return (
    <div className="search-container">
      <Container shadow="large" radius="medium" backgroundColor="overlay">
        <Downshift
          onChange={selection =>
            selection ? (window.location.href = selection.url) : null
          }
        >
          {({ getInputProps, getItemProps, getMenuProps }) => (
            <div className="search-wrapper">
              <Container padding="small" borderSide="bottom">
                <Arrange columns={['1fr', 'auto', 'auto']}>
                  <TextInput
                    {...getInputProps()}
                    value={query}
                    placeholder="Enter your search..."
                    onChange={event => setQuery(event.target.value)}
                    icon={<SvgSearch />}
                    ref={inputElement}
                  />
                  <Spacer left="medium" right="small">
                    <TextButton onClick={resetInput}>Clear</TextButton>
                  </Spacer>
                  <IconButton
                    altText="Close search modal"
                    icon={<SvgClose />}
                    onClick={closeModal}
                  />
                </Arrange>
              </Container>

              {(uiLoading || noResultsFound) && (
                <Container height={7} paddingX="large">
                  <Align alignment="centerLeft">
                    {uiLoading && <Text>Loading...</Text>}
                    {noResultsFound && (
                      <Text>
                        No matches found for{' '}
                        <Text isInline fontWeight="bold">
                          {results.query}
                        </Text>
                      </Text>
                    )}
                  </Align>
                </Container>
              )}

              <ul {...getMenuProps()} className="search-items-list">
                {!uiLoading &&
                  results.list.map((item, index) => {
                    return (
                      <li
                        key={index}
                        className="search-list-item-wrapper"
                        {...getItemProps({
                          key: index,
                          index,
                          item,
                        })}
                      >
                        <a
                          href={item.url}
                          key={item.id}
                          className="search-list-item-link"
                          onClick={event =>
                            handleClickThrough(event, item.id, index)
                          }
                        >
                          <Arrange columns={['auto', '1fr']}>
                            <Container padding="medium">
                              <Icon icon={<SvgPlay />} />
                            </Container>
                            <Container minWidth={0} paddingRight="small">
                              <Text hasEllipsis>{item.name}</Text>
                              <Text size="small">
                                <Arrange>
                                  <div className="details-folder-section">
                                    {item.folder}
                                  </div>
                                  <div className="details-duration-section">
                                    {secondsToVideoTS(item.duration)}
                                  </div>
                                  <div className="details-date-section">
                                    {moment(item.createdAt).format('LL')}
                                  </div>
                                </Arrange>
                              </Text>
                            </Container>
                          </Arrange>
                        </a>
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}
        </Downshift>
      </Container>
    </div>
  );
}

export default SearchModal;
